'use client'

import type { ReactNode } from 'react'
import { BlockStack, InlineStack, Text } from '@shopify/polaris'
import { useSigmaSurface } from '@/components/SigmaSurfaceProvider'

type Props = {
  title: string
  /** Optional subtitle (e.g. date range). Prefer leaving unset for minimal layouts. */
  description?: ReactNode
  /** Right-aligned actions (buttons, links). */
  actions?: ReactNode
  /** Tighter spacing below (for nested groups). */
  tight?: boolean
}

/**
 * Section title, optional description, optional actions.
 */
export function SigmaSectionHeader({ title, description, actions, tight }: Props) {
  const hasActions = actions != null && actions !== false
  const surface = useSigmaSurface()
  const isPortal = surface === 'portal'

  const rootClass = [
    'sigma-section-header',
    tight ? 'sigma-section-header--tight' : '',
    isPortal ? 'sigma-section-header--portal' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rootClass}>
      <InlineStack align="space-between" blockAlign="start" wrap gap="300">
        <BlockStack gap="100">
          <Text as="h2" variant={isPortal ? 'headingLg' : 'headingMd'}>
            {title}
          </Text>
          {description != null && description !== '' ? (
            <Text as="p" variant="bodySm" tone="subdued">
              {description}
            </Text>
          ) : null}
        </BlockStack>
        {hasActions ? (
          <InlineStack gap="200" wrap blockAlign="center">
            {actions}
          </InlineStack>
        ) : null}
      </InlineStack>
    </div>
  )
}
